import React, { useRef, useState } from 'react';
import { StepState, StepType, UploadFlow } from './UploadFlow';
import { Step, StepButton, StepLabel, Stepper, ConfirmDialog } from '@beeline.com/bp-design-system';

import { useRsi } from '../hooks/useRsi';
import { stepIndexToStepType, steps, stepTypeToStepIndex } from '../utils/steps';
import { ModalHeader } from '../components/ModalHeader';

export const Steps = () => {
  const { initialStepState, translations, isNavigationEnabled } = useRsi();

  const initialStep = stepTypeToStepIndex(initialStepState?.type);
  const [activeStep, setStep] = useState(initialStep);

  const [state, setState] = useState<StepState>(initialStepState || { type: StepType.upload });

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const history = useRef<StepState[]>([]);

  const [clickStep, setClickStep] = useState<number | null>(null);

  const doClickStep = (stepIndex: number) => {
    const type = stepIndexToStepType(stepIndex);
    const historyIdx = history.current.findIndex((v) => v.type === type);
    if (historyIdx === -1) return;
    const nextHistory = history.current.slice(0, historyIdx + 1);
    history.current = nextHistory;
    setState(nextHistory[nextHistory.length - 1] as StepState);
    setStep(stepIndex);
  };

  const onClickStep = (stepIndex: number) => {
    if (hasChanged) {
      setShowConfirmDialog(true);
      setClickStep(stepIndex);
    } else {
      doClickStep(stepIndex);
    }
  };

  const onBack = () => {
    setClickStep(null);

    if (hasChanged) {
      setShowConfirmDialog(true);
    } else {
      doClickStep(Math.max(activeStep - 1, 0));
    }
  };

  const onConfirm = () => {
    setHasChanged(false);
    setShowConfirmDialog(false);
    if (clickStep) {
      doClickStep(clickStep);
    } else {
      doClickStep(Math.max(activeStep - 1, 0));
    }
  };

  const onNext = (v: StepState) => {
    history.current.push(state);
    setState(v);
    v.type !== StepType.selectSheet && setStep((prev) => prev + 1);
  };

  const isInHistory = (stepIndex: number) => {
    return history.current.findIndex((v) => v.type === stepIndexToStepType(stepIndex)) !== -1;
  };

  return (
    <>
      <ModalHeader>
        <Stepper activeStep={activeStep}>
          {steps.map((key, stepIndex) => {
            return (
              <Step key={key} completed={stepIndex < activeStep}>
                {isNavigationEnabled ? (
                  <StepButton
                    disabled={stepIndex === activeStep || !isInHistory(stepIndex)}
                    onClick={() => onClickStep(stepIndex)}
                  >
                    {translations[key].title}
                  </StepButton>
                ) : (
                  <StepLabel>{translations[key].title}</StepLabel>
                )}
              </Step>
            );
          })}
        </Stepper>
      </ModalHeader>
      <UploadFlow
        state={state}
        onNext={onNext}
        onBack={isNavigationEnabled ? onBack : undefined}
        setHasChanged={setHasChanged}
      />
      <ConfirmDialog
        open={showConfirmDialog}
        onClose={() => {
          setShowConfirmDialog(false);
          setClickStep(null);
        }}
        title={translations.alerts.confirmBack.title}
        subTitle={translations.alerts.confirmBack.subTitle}
        onSubmit={onConfirm}
        slotProps={{
          submitButton: {
            color: 'secondary',
            children: translations.alerts.confirmBack.goBack
          }
        }}
      />
    </>
  );
};
